import {
  escapeHtml,
  renderBadge,
  renderButton,
  renderMetricCard,
  renderProductCard,
  renderStatusCard,
} from "../../../design-system/components";
import { showcaseBaseTheme, themes } from "../../../design-system/themes";
import type { ComponentCategory, ShowcaseComponent } from "../domain/showcase-component";
import { layoutAttributes } from "@mcebey/design-system/layout/html";
import { DSStack } from "@mcebey/design-system/layout/recipes";
import { DSCoreDimension as D } from "@mcebey/design-system/layout/tokens";

export interface ShowcaseState {
  readonly category: ComponentCategory;
  readonly componentId?: string;
  readonly view?: "home" | "common" | "products" | "pages" | "themes";
  readonly themeId: string;
  readonly query?: string;
  readonly analyticsCount?: number;
}

const categories: ReadonlyArray<{ readonly id: ComponentCategory; readonly label: string }> = [
  { id: "common", label: "Common" },
  { id: "push", label: "Push" },
  { id: "agenda", label: "Agenda Galicia" },
  { id: "opo", label: "OpoRacha" },
  { id: "visagismo", label: "Visagismo" },
];

function fieldLayoutAttributes(error = false): string {
  return layoutAttributes(DSStack({
    className: error ? "field field--error" : "field",
    gap: D.componentNativeWebLegacyFieldGap,
    align: "stretch",
  }));
}

export function renderShowcase(
  title: string,
  components: ReadonlyArray<ShowcaseComponent>,
  state: ShowcaseState = { category: "common", themeId: "showcase-base", query: "", view: "home" },
): string {
  const selected = components.find((component) => component.id === state.componentId);
  const view = state.view ?? "home";
  return `
    <header class="topbar">
      <a class="brand" href="#contenido" data-action="home">${escapeHtml(title)}</a>
      <span class="environment" aria-label="Entorno de desarrollo">DEV</span>
    </header>
    <nav class="app-nav" aria-label="Secciones principales">
      ${(["home", "common", "products", "pages", "themes"] as const).map((destination, index) => `
        <button type="button" data-action="navigate" data-view="${destination}" aria-current="${view === destination ? "page" : "false"}">
          <span aria-hidden="true">${["⌂", "▦", "◈", "▤", "◐"][index]}</span>${["Inicio", "Common", "Productos", "Páginas", "Temas"][index]}
        </button>
      `).join("")}
    </nav>
    <div id="contenido" class="showcase-content">
      ${selected ? renderDetail(selected, state) : view === "home" ? renderOverview(components)
        : view === "pages" ? renderPageExamples()
          : view === "themes" ? renderThemes(state.themeId)
            : renderCatalog(components, view === "common" ? "common" : state.category, state.query ?? "", view)}
    </div>
  `;
}

function renderHeading(eyebrow: string, title: string, summary: string): string {
  return `<div class="page-heading"><p class="eyebrow">${eyebrow}</p><h1>${title}</h1><p>${summary}</p></div>`;
}

function renderOverview(components: ReadonlyArray<ShowcaseComponent>): string {
  const count = components.filter((component) => component.category === "common").length;
  return `<section class="stack-lg">
    ${renderHeading(
      "Design System", "Una base, productos con identidad",
      "Explora qué reutilizamos, qué pertenece a cada app y cómo se compone una pantalla real.",
    )}
    <div class="overview-metrics">
      ${renderMetricCard("Inventario común", String(count), "contrato canónico")}
      ${renderMetricCard("Disponibles iOS", "13", "con preview")}
      ${renderMetricCard("Categorías", "9", "Common")}
      ${renderMetricCard("Extensiones", "4", "por producto")}
    </div>
    <h2>Fronteras</h2>
    ${renderStatusCard("Common", "Tokens, temas y componentes genéricos")}
    ${renderStatusCard("Producto → Common", "Push, Agenda, Opo y Visagismo")}
    ${renderStatusCard("App Showcase", "Consume ambos niveles; no contiene el DS")}
    ${renderStatusCard("Catálogo honesto", "La disponibilidad de un componente se comprueba en su plataforma.")}
  </section>`;
}

function renderThemes(selectedThemeId: string): string {
  return `<section class="stack-lg">
    ${renderHeading("Temas", "Direcciones visuales", "Cambian color y contraste sin romper la semántica.")}
    ${renderThemeSelector(selectedThemeId)}
    <div class="theme-grid">${[showcaseBaseTheme, ...themes].map((theme) => `
      <button type="button" class="theme-card" data-action="choose-theme"
        data-theme="${theme.id}" aria-pressed="${theme.id === selectedThemeId}">
        <span class="theme-swatch" style="--swatch:${theme.primary};--swatch-bg:${theme.surface}"></span>
        <strong>${escapeHtml(theme.name)}</strong><small>${escapeHtml(theme.category)}</small>
      </button>`).join("")}</div>
  </section>`;
}

function renderPageExamples(): string {
  return `
    <section class="detail stack-lg" aria-labelledby="pages-title">
      ${renderHeading("Composición", "Ejemplos de página", "Componentes colocados en un flujo para validar jerarquía, ritmo y estados.")}
      <div>
        <p class="eyebrow">Páginas</p>
        <h2 id="pages-title" tabindex="-1">Páginas de producto</h2>
      </div>
      <div class="component-grid">
        ${renderProductCard("Push", "Entrenamiento en curso", "Serie 2 de 4 · progreso y descanso visibles")}
        ${renderProductCard("Agenda Galicia", "Tu plan para hoy", "Eventos próximos, clima y distancia")}
        ${renderProductCard("OpoRacha", "Reto diario", "Secuencia de preguntas con explicación")}
        ${renderProductCard("Visagismo", "Tu guía personal", "Recomendaciones y alternativas visuales")}
      </div>
      ${renderStatusCard("Contrato compartido", "La misma jerarquía, estados y lenguaje visual se reutilizan en iOS, Android y web.")}
    </section>
  `;
}

function renderThemeSelector(selectedThemeId: string): string {
  const grouped = Map.groupBy([showcaseBaseTheme, ...themes], (theme) => theme.category);
  const options = [...grouped.entries()].map(([category, values]) => `
    <optgroup label="${escapeHtml(category)}">
      ${values.map((theme) => `
        <option value="${theme.id}"${theme.id === selectedThemeId ? " selected" : ""}>${escapeHtml(theme.name)}</option>
      `).join("")}
    </optgroup>
  `).join("");
  return `
    <label class="theme-picker" for="theme-select">
      <span>Tema visual</span>
      <select id="theme-select" data-action="select-theme">${options}</select>
      <small>Tema base + 30 variantes · 6 categorías</small>
    </label>
  `;
}

function renderCatalog(
  components: ReadonlyArray<ShowcaseComponent>,
  selectedCategory: ComponentCategory,
  query: string,
  view: "common" | "products",
): string {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visible = components.filter((component) => component.category === selectedCategory)
    .filter((component) => normalizedQuery.length === 0 || [
      component.title,
      component.description,
      component.canonicalCategory ?? "",
    ].some((value) => value.toLocaleLowerCase().includes(normalizedQuery)));
  return `
    <section aria-labelledby="components-title" class="stack-lg">
      ${renderHeading(
        view === "common" ? "Common" : "Extensiones",
        view === "common" ? "Componentes reutilizables" : "Lenguaje de producto",
        view === "common"
          ? "Los componentes de esta sección no conocen ningún producto."
          : "Patrones que reutilizan Common, pero solo tienen sentido dentro de una familia.",
      )}
      <div class="section-heading">
        <div>
          <p class="eyebrow">Biblioteca</p>
          <h2 id="components-title" tabindex="-1">${view === "common" ? "Inventario completo" : "Familias de producto"}</h2>
        </div>
        <p>Selecciona un elemento para ver sus configuraciones.</p>
      </div>
      <div class="tabs" role="tablist" aria-label="Categoría de componentes">
        ${categories.filter((category) => view === "products" ? category.id !== "common" : category.id === "common").map((category) => `
          <button
            class="tab"
            type="button"
            role="tab"
            aria-selected="${category.id === selectedCategory}"
            data-action="select-category"
            data-category="${category.id}"
          >${escapeHtml(category.label)}</button>
        `).join("")}
      </div>
      <label${fieldLayoutAttributes()} for="component-search">
        <span>Buscar componente o tipo</span>
        <input
          id="component-search"
          type="search"
          value="${escapeHtml(query)}"
          data-action="search-components"
          placeholder="Nombre o categoría"
        />
      </label>
      <p class="results-count">${visible.length} resultados</p>
      ${visible.length === 0 ? renderStatusCard("Sin resultados", "Prueba con otro nombre o categoría.") : ""}
      <div class="component-grid">${visible.map(renderComponentCard).join("")}</div>
    </section>
  `;
}

function renderComponentCard(component: ShowcaseComponent): string {
  return `
    <button class="component-card" type="button" data-action="open-component" data-component="${component.id}">
      <span>${renderBadge(`${component.configurations} estados`)}</span>
      <strong>${escapeHtml(component.title)}</strong>
      <span>${escapeHtml(component.description)}</span>
      <span class="component-link">Abrir ejemplos →</span>
    </button>
  `;
}

function renderDetail(component: ShowcaseComponent, state: ShowcaseState): string {
  return `
    <section class="detail stack-lg" aria-labelledby="detail-title">
      ${renderButton("← Volver al catálogo", "text", false, 'data-action="back" data-ds-padding-inline="none"')}
      <div>${renderBadge(categoryLabel(component.category), true)}</div>
      <div>
        <h2 id="detail-title" tabindex="-1">${escapeHtml(component.title)}</h2>
        <p>${escapeHtml(component.description)}</p>
      </div>
      <div class="examples stack-md">${renderExamples(component, state)}</div>
    </section>
  `;
}

function renderExamples(component: ShowcaseComponent, state: ShowcaseState): string {
  switch (component.kind) {
    case "canonical": return `
      <div class="canonical-preview">
        ${renderBadge(component.canonicalCategory ?? "Common", true)}
        ${renderStatusCard(component.title, "Ficha canónica del Design System · estados, accesibilidad y comportamiento definidos.")}
        ${renderButton("Estado principal")}
        ${renderButton("Estado alternativo", "secondary")}
        ${renderButton("Ver contrato", "tonal")}
      </div>
    `;
    case "buttons": return [
      renderButton("Acción principal"),
      renderButton("Acción secundaria", "secondary"),
      renderButton("Acción tonal", "tonal"),
      renderButton("Eliminar", "danger"),
      renderButton("Acción de texto", "text"),
      renderButton("Procesando…", "primary", true),
      renderButton("No disponible", "secondary", true),
    ].join("");
    case "inputs": return renderInputExamples();
    case "cards": return [
      renderStatusCard("Contenido", "Una tarjeta agrupa información relacionada."),
      renderStatusCard("Con acción", "La acción explica su resultado.", "Ver detalle"),
      renderMetricCard("Progreso semanal", "3 de 4", "Una sesión para completar el objetivo", 75),
      renderProductCard("Common", "Tarjeta destacada", "Acento semántico sin perder contraste"),
    ].join("");
    case "feedback": return [
      renderStatusCard("Cargando planes", "Mantenemos el contexto mientras llegan los datos."),
      renderStatusCard("Sin resultados", "Cambia los filtros o amplía la distancia.", "Limpiar filtros"),
      renderStatusCard("No se pudo actualizar", "Comprueba la conexión y vuelve a intentarlo.", "Reintentar"),
      renderStatusCard("Guardado", "El cambio está disponible sin conexión."),
      renderMetricCard("Sincronización", "80 %", "4 de 5 bloques listos", 80),
    ].join("");
    case "navigation": return `
      <nav class="demo-nav" aria-label="Navegación de ejemplo">
        <a href="#inicio" aria-current="page">Inicio</a><a href="#biblioteca">Biblioteca</a><a href="#perfil">Perfil</a>
      </nav>
      ${renderStatusCard("Destino: Inicio", "La selección siempre tiene texto y estado visible.")}
      ${renderButton("Abrir detalle", "tonal")}
    `;
    case "platform-capabilities": return `
      ${renderMetricCard(
        "component_opened",
        String(state.analyticsCount ?? 0),
        "localOnly · sin red",
        Math.min((state.analyticsCount ?? 0) * 10, 100),
      )}
      ${renderButton("Borrar contador", "secondary", false, 'data-action="clear-analytics"')}
      ${renderStatusCard("blocked", "Kill switch o flujo sensible")}
      ${renderStatusCard("loading", "Reserva estable mientras carga")}
      ${renderStatusCard("ready", "Creatividad del proveedor Test lista")}
      ${renderStatusCard("empty", "Sin inventario; el flujo continúa")}
      ${renderStatusCard("failed", "Error aislado y reintentable")}
    `;
    case "push-workout": return `
      ${renderProductCard("Push", "Press de banca", "Serie 2 de 4 · 70 kg × 8")}
      ${renderMetricCard("Entrenamiento", "45 %", "3 de 7 ejercicios completados", 45)}
      ${renderButton("Registrar serie")}
    `;
    case "agenda-plan": return `
      ${renderProductCard("Agenda Galicia", "Festival de jazz", "Hoy · 20:30 · Vigo · a 2,4 km")}
      <div>${renderBadge("Vigente", true)}</div>
      ${renderStatusCard("Por qué aparece primero", "Empieza pronto, está cerca y la fuente se actualizó hoy.")}
    `;
    case "opo-question": return `
      ${renderProductCard("OpoRacha", "Psicobiología", "Pregunta 2 de 5 · dificultad media")}
      ${renderStatusCard("Respuesta explicada", "La opción B relaciona la estructura con su función principal.")}
      ${renderButton("Siguiente pregunta")}
    `;
    case "visagismo-guide": return `
      ${renderProductCard("Visagismo", "Opción más probable", "Montura rectangular fina · confianza orientativa")}
      ${renderStatusCard("Qué observar", "La línea superior definida aporta estructura sin cubrir la ceja.")}
      ${renderButton("Comparar dos alternativas", "secondary")}
    `;
  }
}

function renderInputExamples(): string {
  return `
    <label${fieldLayoutAttributes()}><span>Normal</span><input value="Press de banca" /><small>Se conserva al guardar.</small></label>
    <label${fieldLayoutAttributes(true)}>
      <span>Con error</span><input aria-invalid="true" />
      <small>Este campo es obligatorio.</small>
    </label>
    <label${fieldLayoutAttributes()}><span>Deshabilitado</span><input value="Dato sincronizado" disabled /></label>
    <label${fieldLayoutAttributes()}><span>Búsqueda</span><input type="search" placeholder="Nombre o categoría" /></label>
  `;
}

function categoryLabel(category: ComponentCategory): string {
  return categories.find((candidate) => candidate.id === category)?.label ?? category;
}
